// These helpers are calling this template's own server-side routes
// so, they are not directly calling Marketplace API or Integration API.
// You can find these api endpoints from 'server/api/...' directory

import appSettings from '../config/settings';
import { types as sdkTypes, transit } from './sdkLoader';
import Decimal from 'decimal.js';

export const apiBaseUrl = marketplaceRootURL => {
  const port = process.env.REACT_APP_DEV_API_SERVER_PORT;
  const useDevApiServer = process.env.NODE_ENV === 'development' && !!port;

  if (useDevApiServer) {
    return `http://localhost:${port}`;
  }

  return marketplaceRootURL ? marketplaceRootURL.replace(/\/$/, '') : `${window.location.origin}`;
};

export const typeHandlers = [
  {
    type: sdkTypes.BigDecimal,
    customType: Decimal,
    writer: v => new sdkTypes.BigDecimal(v.toString()),
    reader: v => new Decimal(v.value),
  },
];

const serialize = data => {
  return transit.write(data, { typeHandlers, verbose: appSettings.sdk.transitVerbose });
};

const deserialize = str => {
  return transit.read(str, { typeHandlers });
};

const methods = {
  POST: 'POST',
  GET: 'GET',
  PUT: 'PUT',
  PATCH: 'PATCH',
  DELETE: 'DELETE',
};

const request = (path, options = {}) => {
  const url = `${apiBaseUrl()}${path}`;
  const { credentials, headers, body, ...rest } = options;
  const shouldSerializeBody =
    (!headers || headers['Content-Type'] === 'application/transit+json') && body;
  const bodyMaybe = shouldSerializeBody ? { body: serialize(body) } : {};

  const fetchOptions = {
    credentials: credentials || 'include',
    headers: headers || { 'Content-Type': 'application/transit+json' },
    ...bodyMaybe,
    ...rest,
  };

  return window.fetch(url, fetchOptions).then(res => {
    const contentTypeHeader = res.headers.get('Content-Type');
    const contentType = contentTypeHeader ? contentTypeHeader.split(';')[0] : null;
    if (res.status >= 400) {
      return res.json().then(data => {
        let e = new Error();
        e = Object.assign(e, data);
        throw e;
      });
    }
    if (contentType === 'application/transit+json') {
      return res.text().then(deserialize);
    } else if (contentType === 'application/json') {
      return res.json();
    }
    return res.text();
  });
};

const post = (path, body, options = {}) => {
  const requestOptions = {
    ...options,
    method: methods.POST,
    body,
  };
  return request(path, requestOptions);
};

export const transactionLineItems = body => post('/api/transaction-line-items', body);
export const initiatePrivileged = body => post('/api/initiate-privileged', body);
export const transitionPrivileged = body => post('/api/transition-privileged', body);
export const notifyFounderOfDispute = transactionId =>
  post('/api/notify-founder-dispute', { transactionId });
export const notifyFounderOfProviderCancellation = transactionId =>
  post('/api/notify-founder-provider-cancellation', { transactionId });
export const notifyProviderOfBookingRequest = transactionId =>
  post('/api/notify-provider-booking-request', { transactionId });
export const notifyCustomerOfBookingDecision = transactionId =>
  post('/api/notify-customer-booking-decision', { transactionId });
export const sendContactEnquiry = values => post('/api/send-contact-enquiry', values);
export const protectLocation = address => post('/api/protect-location', { address });
export const revealBookingLocation = transactionId =>
  post('/api/reveal-booking-location', { transactionId });
export const createUserWithIdp = body => post('/api/auth/create-user-with-idp', body);
export const deleteUserAccount = body => post('/api/delete-account', body);
